import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { getUserFolders } from '../../services/folders';
import { updateItem } from '../../services/items';
import type { Item, Folder } from '../../types';

interface AddToFolderDialogProps {
  open: boolean;
  onClose: () => void;
  item: Item;
  onUpdated: () => void;
}

export default function AddToFolderDialog({ open, onClose, item, onUpdated }: AddToFolderDialogProps) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    getUserFolders(user.uid).then((cols) => {
      setFolders(cols);
      setSelected(new Set(item.folderIds));
      setLoading(false);
    });
  }, [open, user, item.folderIds]);

  const handleToggle = (folderId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateItem(item.id, { folderIds: [...selected] });
      onClose();
      onUpdated();
    } catch (err) {
      console.error('Failed to update folders:', err);
      alert('Failed to update folders. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" fullScreen={mobile}>
      <DialogTitle>Add to Folder</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : folders.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No folders yet. Create one from the Folders page.
          </Typography>
        ) : (
          <List dense>
            {folders.map((col) => (
              <ListItem key={col.id} disablePadding>
                <ListItemButton onClick={() => handleToggle(col.id)}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={selected.has(col.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={col.name} secondary={col.description || undefined} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
